/**
 * `/login` — `mockups/client/login.html`, working.
 *
 * The mockup shows both states at once: the form, and the "what you see after you
 * submit" notice folded underneath it. Here they are one screen in two states, and
 * the fold is only offered before you have submitted — once you have, you are
 * looking at the thing it was describing.
 *
 * Five behaviours are worth reading before changing anything:
 *
 *   1. **An address with no account is told so.** `/sign-in/magic-link` refuses it
 *      with `NO_ACCOUNT_CODE`, and the form says what a refused Google sign-in says
 *      — `NOT_INVITED`, one sentence for both doors. Until 14 September 2026 the
 *      answer was the same for every address, so that the form could not be used to
 *      learn which were real; `.docs/04-auth-clients.md` §Edge cases has the
 *      decision. A throttled address is still answered as a sent one: a link did go
 *      out less than a minute ago, and the countdown already says when another can.
 *   2. **The countdown is a deadline, not a counter.** `.docs/08-web-foundation.md`
 *      §Edge cases: "Resend countdown drifts on tab sleep — compute from the
 *      throttle's `retry_after` timestamp, not a decrementing counter." The
 *      throttle answers as a sent link does (see 1), so the deadline is computed
 *      here from the moment we asked plus `MAGIC_LINK_RESEND_SECONDS` — the same
 *      constant the API throttles on, which is why it lives in `packages/shared`.
 *      The interval re-reads the clock rather than subtracting one, so a laptop
 *      that slept for ten minutes comes back with the button live.
 *   3. **`?redirect=` is validated before it is used.** It is a URL somebody else
 *      may have written, and it becomes the `callbackURL` a signed-in browser is
 *      sent to. Only a path on this origin is accepted; anything else falls back
 *      to `/`, which sends you to your own portal. It is then made absolute
 *      against this origin — see `callbackUrlFor`.
 *   3b. **An OAuth flow that a sign-in interrupted is resumed, and `?redirect=` is
 *      not how.** Better Auth's OAuth provider sends a signed-out person here with
 *      the whole signed authorization request in the query string and no `redirect`
 *      on it at all, so this page would otherwise sign them in and land them on
 *      their dashboard while the CLI that opened the browser waited out its own
 *      timeout. `landingFor` is where that is decided; `auth/oauth.ts`
 *      `resumeAuthorizeUrl` is what it lands on.
 *   4. **The markup is the design system's.** Session 08 wrote the contract's
 *      classes here by hand and said session 09 would replace them; it has.
 *      `Splash`, `SplashBrand`, `SplashCard`, `Input`, `Button`, `Notice`, `Fold`
 *      and the text primitives are all this file uses, and there is no `class`
 *      attribute left in it. The form is react-hook-form over the same zod check
 *      the hand-written version made.
 *   5. **A provider sign-in has no success state, and `busy` is never cleared on
 *      it.** `signIn.social` navigates the whole document: on the happy path this
 *      component stops existing, so there is nothing to set back. That is why
 *      `leaveFor` only clears `busy` in the failure branch — a "Sign in with
 *      Google" button that went back to normal would mean the request failed, and
 *      leaving it disabled is what stops a second click opening a second flow while
 *      the first is still redirecting. Whether the person is actually signed in is
 *      decided at `/api/auth/callback/:id` and reported back here as
 *      `?error=…` — or not at all, because they landed on their dashboard.
 */

import {
  MAGIC_LINK_RESEND_SECONDS,
  MAGIC_LINK_TTL_SECONDS,
  NO_ACCOUNT_CODE,
  addSeconds,
  formatCountdown,
  type SocialProvider,
} from '@fareast/shared'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'

import { useAuthActions } from '../auth/actions.js'
import { homeFor } from '../auth/guards.js'
import { useAuthMethods } from '../auth/methods.js'
import { resumeAuthorizeUrl } from '../auth/oauth.js'
import { Button, ButtonRow, Or, ProviderButton } from '../components/portal/Button.js'
import { Fold } from '../components/portal/Disclosure.js'
import { Input, useZodForm } from '../components/portal/Form.js'
import { Stack } from '../components/portal/Layout.js'
import { Splash, SplashBrand, SplashCard, SplashForm } from '../components/portal/Splash.js'
import { Notice } from '../components/portal/Status.js'
import { B, Dim, HeroCap, Linkish, Muted, Prose, Small, Xs } from '../components/portal/Text.js'
import { now } from '../integrations/clock.js'
import { absoluteUrl, currentSearch, leaveTheSpa } from '../integrations/navigate.js'
import { routeSearch } from '../search/useSearchState.js'
import { useBodyClass, useDocumentTitle } from '../shell/useBodyClass.js'

const LoginSearchSchema = z.object({
  /** Where to go once the link is followed. A path on this origin, or nothing. */
  redirect: z.string().optional(),
  /**
   * Why the last attempt did not work. `?error=signup_disabled` and friends.
   *
   * **`z.string()`, not `z.enum`,** and the difference is the whole front door. The
   * codes come from Better Auth and from the provider — `access_denied` when somebody
   * clicks Cancel is Google's word, not ours — so the list is not ours to close. A
   * schema that refused an unknown one would throw a search-validation error and
   * replace `/login` with `RouteError`: the person who could not sign in would then
   * also not be able to try again. An unknown code gets the general sentence.
   */
  error: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  /**
   * `routeSearch`, for the reason the `error` comment gives one paragraph up: on
   * this route a search-validation throw is a person who cannot sign in and cannot
   * see why. Open strings are the first line of that defence and this is the
   * second — the router parses a search value with `JSON.parse`, so
   * `?error={"x":1}` reaches `z.string()` as an object however open the union is.
   */
  validateSearch: routeSearch(LoginSearchSchema),
  beforeLoad: ({ context, search }) => {
    const { principal } = context.session
    if (principal.kind === 'anonymous') return
    /*
     * Already signed in: `.docs/08-web-foundation.md` §10 — with one case in front
     * of it. Somebody who signed in in another tab and then reloaded this one is
     * holding a session *and* an interrupted OAuth flow, and sending them to their
     * dashboard would abandon it. `/api/auth/oauth2/authorize` is not a route in
     * this app, so resuming is a full document load; the component renders for the
     * moment before the browser leaves, which is what every `leaveTheSpa` does.
     */
    const resume = resumeAuthorizeUrl(currentSearch())
    if (resume !== null) {
      leaveTheSpa(resume)
      return
    }
    throw redirect({ to: safeRedirect(search.redirect) ?? homeFor(principal) })
  },
  component: LoginPage,
})

/**
 * A same-origin path, or null.
 *
 * The naive check — "starts with a slash and not two" — is **not enough**, and the
 * two ways past it are not obvious:
 *
 *   - `/\evil.example`. The URL parser reads a backslash as a slash, so this is
 *     `//evil.example` and resolves to `http://evil.example/`.
 *   - `/<tab>/evil.example`. It strips tabs and newlines *before* parsing, so this
 *     is `//evil.example` too.
 *
 * The only check that survives both is asking the parser where the value actually
 * lands, and keeping what it resolved to rather than what was typed. Better Auth
 * checks `callbackURL` against `trustedOrigins` as well; this is the half that does
 * not depend on that list being right, and it is also what protects the router's
 * own `redirect({ to })`, which never sees that list at all.
 */
export function safeRedirect(value: string | undefined): string | null {
  if (value === undefined || value === '') return null
  if (!value.startsWith('/') || value.startsWith('//')) return null

  const here = window.location.origin
  let resolved: URL
  try {
    resolved = new URL(value, here)
  } catch {
    return null
  }
  if (resolved.origin !== here) return null

  // What it resolved to, not what was typed: normalised, and still carrying the
  // fragment, which is half of a deep link.
  return `${resolved.pathname}${resolved.search}${resolved.hash}`
}

/**
 * Where a completed sign-in lands, as a same-origin path.
 *
 * Three answers in order, and the first is the one this function exists for.
 *
 *   1. **An OAuth flow this page interrupted.** The provider redirects a signed-out
 *      person here with the whole signed authorization request in the query string
 *      and no `?redirect=` on it — it does not know this app's addresses, only the
 *      `loginPage` it was configured with. So the request is the landing:
 *      `/api/auth/oauth2/authorize` with the signature stripped off
 *      (`auth/oauth.ts` `resumeAuthorizeUrl`), which re-runs authorization with the
 *      session that now exists and lands on `/oauth/consent`. Without this the
 *      person signs in, arrives at their dashboard, and the CLI that opened the
 *      browser waits out its own timeout with nothing to show for it.
 *   2. `?redirect=`, validated — a deep link somebody followed while signed out.
 *   3. `/`, which is the redirect to their own portal.
 *
 * The whole *raw* query is what decides 1, not `search`: `validateSearch` knows two
 * keys and the signature covers eleven. `integrations/navigate.ts` `currentSearch`
 * is where it comes from, and is why this takes it as an argument rather than
 * reading it — a page test says what the address bar holds by passing a string.
 */
export function landingFor(wanted: string | undefined, search: string): string {
  return resumeAuthorizeUrl(search) ?? safeRedirect(wanted) ?? '/'
}

/**
 * Where the magic link should land, as an absolute URL on **this** origin.
 *
 * Better Auth resolves a relative `callbackURL` against its own base
 * (`new URL(callbackURL, ctx.context.baseURL)` in the magic-link plugin), which
 * is the API. In production that is the same origin as the SPA and either
 * spelling works; in development the API is :3000 and the SPA is :5173, so a
 * relative `/app` would land on the API and 404. The origin the browser is
 * actually on is the one fact this side of the wire has and the server does not.
 */
export function callbackUrlFor(path: string): string {
  return absoluteUrl(path)
}

/**
 * Where a **refused** social sign-in comes back to: this page, with `?redirect=` still
 * on it, so a deep link survives a failed attempt and the retry lands where the person
 * was going.
 *
 * Absolute for the same reason `callbackUrlFor` is — Better Auth resolves a relative
 * one against its own base, which is the API — and it is a separate URL because a
 * refusal never reaches the `callbackURL`. Better Auth appends `error=…` with the
 * right separator, so the query string here is left as it is.
 */
export function errorCallbackUrlFor(wanted: string | undefined, search: string): string {
  // An interrupted OAuth flow comes back with its **own** query still on it, which
  // is the only way the retry can resume: `?redirect=` cannot carry a signed request
  // through `validateSearch`, and Better Auth appends `error=…` with the right
  // separator, so the query string is left exactly as it arrived.
  if (resumeAuthorizeUrl(search) !== null) return absoluteUrl(`/login${search}`)

  const safe = safeRedirect(wanted)
  return absoluteUrl(safe === null ? '/login' : `/login?redirect=${encodeURIComponent(safe)}`)
}

/* ------------------------------------------------------------- what went wrong */

const REFUSAL_GENERAL = 'We could not sign you in. Try again, or ask for a sign-in link below.'

/**
 * The invited-only property, seen from the outside — **one sentence for three codes**:
 * the magic link's `NO_ACCOUNT_CODE`, and the social callback's `signup_disabled` and
 * `email_not_found`.
 *
 * The first two are one fact reaching the screen by two doors: nobody has an account at
 * that address. Until 14 September 2026 only the social door said so — the magic link
 * answered an unknown address as if it had sent something, so that a stranger with a
 * form could not learn which addresses were real — and the owner chose the answer over
 * the oracle (`.docs/04-auth-clients.md` §Edge cases).
 *
 * The third reads the same on purpose. "Nobody invited you" and "your provider did not
 * tell us your address" are two failures with the same remedy, and there is no reason
 * to let the page be an oracle about which.
 */
const NOT_INVITED =
  'That address has not been invited to the portal. Ask whoever owns your account to ' +
  'invite it, or write to hello@madebyfareast.com.'

/**
 * A refusal code, in the "Trouble signing in?" voice.
 *
 * Not exhaustive and cannot be: provider errors pass through verbatim, so
 * `access_denied` — somebody clicking Cancel — arrives as a code that is not in here
 * and gets `REFUSAL_GENERAL`, which reads sensibly for it.
 */
export const REFUSALS: Readonly<Record<string, string>> = {
  // The invited-only property, by either door. See `NOT_INVITED`.
  [NO_ACCOUNT_CODE.toLowerCase()]: NOT_INVITED,
  signup_disabled: NOT_INVITED,
  email_not_found: NOT_INVITED,

  // The provider would not vouch for the mailbox. On Microsoft this is `xms_edov`,
  // and it is the refusal that stops somebody signing in from a tenant that has not
  // verified the domain your address is on.
  account_not_linked:
    'Your provider did not confirm that you own that email address, so we did not sign ' +
    'you in. Use the sign-in link above instead.',
  email_not_verified:
    'That email address is not verified with your provider. Verify it there, or use the ' +
    'sign-in link above.',
  email_does_not_match: 'That account uses a different email address from your portal login.',
  account_already_linked_to_different_user:
    'That account is already connected to a different portal login.',

  // The handshake did not complete. The first is also what a lapsed Microsoft client
  // secret looks like, which is worth knowing when everybody reports it at once.
  invalid_code: 'That sign-in did not complete. Try again, or use the sign-in link above.',
  no_code: 'That sign-in did not complete. Try again, or use the sign-in link above.',
  invalid_callback_request:
    'Something went wrong coming back from your provider. Try again, or use the sign-in ' +
    'link above.',

  // The state expired or did not match. The commonest cause is leaving the provider's
  // page open: the state cookie lasts five minutes.
  state_mismatch: 'That took too long and the sign-in expired. Try again.',
  state_not_found: 'That took too long and the sign-in expired. Try again.',
  state_invalid: 'That took too long and the sign-in expired. Try again.',

  // We could not tell who signed in.
  unable_to_get_user_info:
    'Your provider did not tell us who you are. Try again, or use the sign-in link above.',
  issuer_mismatch: 'We could not verify who signed you in. Use the sign-in link above.',
  unable_to_link_account:
    'We could not connect that account to your portal login. Use the sign-in link above.',

  // The provider is not configured on this deployment at all.
  oauth_provider_not_found:
    'That way of signing in is not switched on for this portal. Use the sign-in link above.',

  /**
   * The magic link's own refusal, and **this is a bug this change fixes rather than a
   * case it adds**: `client-signin.spec.ts` proves a replayed link already 302s here
   * with `?error=INVALID_TOKEN`, and `LoginSearchSchema` stripped it — so that person
   * saw a login page that said nothing at all about why they were back on it.
   *
   * Upper-case, because Better Auth sends it that way. `refusalFor` lower-cases before
   * it looks anything up.
   */
  invalid_token:
    'That sign-in link has already been used, or its fifteen minutes are up. Ask for a ' +
    'new one below.',
}

/** Our sentence for a code, if we have one. Case-insensitive: Better Auth sends both. */
function sentenceFor(code: string): string | undefined {
  return REFUSALS[code.trim().toLowerCase()]
}

/** The sentence for a code on the URL, or the general one. Never throws. */
export function refusalFor(code: string | undefined): string | null {
  if (code === undefined || code.trim() === '') return null
  return sentenceFor(code) ?? REFUSAL_GENERAL
}

/**
 * What to say when asking for a link was refused: our sentence for a code we have one
 * for — `NO_ACCOUNT_CODE` is the one that matters — and Better Auth's own message for
 * anything else.
 *
 * Not `refusalFor`, whose fallback is `REFUSAL_GENERAL`: a code on the URL arrives with
 * no words at all, while a refused request carries a message of its own — a validation
 * failure, say — that says more than the general sentence would.
 */
export function problemFor(result: { readonly code?: string; readonly message: string }): string {
  return (result.code === undefined ? undefined : sentenceFor(result.code)) ?? result.message
}

type Sent = { readonly email: string; readonly resendAt: Date }

function LoginPage() {
  useBodyClass('splash')
  useDocumentTitle('Sign in — Far East Studios')

  const { redirect: wanted, error } = Route.useSearch()
  const search = currentSearch()
  const callbackURL = callbackUrlFor(landingFor(wanted, search))
  const errorCallbackURL = errorCallbackUrlFor(wanted, search)
  const auth = useAuthActions()
  const methods = useAuthMethods()

  const [sent, setSent] = useState<Sent | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /*
   * Whether this page has asked for a link yet. `?error=` is about the attempt that
   * sent the person here; once they have asked for a link, what *that* answered is the
   * news. Without it a refused Google sign-in followed by an address with no account
   * shows `NOT_INVITED` twice, in two alerts, one of them about the past.
   */
  const [asked, setAsked] = useState(false)

  const refusal = asked ? null : refusalFor(error)

  async function request(address: string): Promise<void> {
    setBusy(true)
    setAsked(true)
    setProblem(null)
    const result = await auth.signInWithMagicLink(address, callbackURL)
    setBusy(false)
    if (!result.ok) {
      setProblem(problemFor(result))
      return
    }
    setSent({ email: address, resendAt: addSeconds(now(), MAGIC_LINK_RESEND_SECONDS) })
  }

  /**
   * Leave for the provider. See note 5: `busy` is deliberately never cleared on the
   * happy path, because there is no happy path *here* — the document is gone.
   */
  function leaveFor(provider: SocialProvider): void {
    setBusy(true)
    setProblem(null)
    void auth.signInWithProvider(provider, { callbackURL, errorCallbackURL }).then((result) => {
      if (!result.ok) {
        setBusy(false)
        setProblem(result.message)
      }
    })
  }

  return (
    <Splash>
      <SplashBrand
        label="Client portal"
        title="Everything we made for you, what it was worth, and what it cost."
        lede="Deliverables, allowances, invoices and approvals in one place."
      >
        <Stack gap="tight">
          <div>
            <HeroCap>Support</HeroCap>
            <Small>hello@madebyfareast.com</Small>
          </div>
          <div>
            <HeroCap>Portal</HeroCap>
            <Small>portal.madebyfareast.com</Small>
          </div>
        </Stack>
      </SplashBrand>

      <SplashForm>
        <SplashCard
          title="Welcome back"
          lede="No passwords. Enter the address we invoice and we will email you a link that signs you straight in."
        >
          {/*
           * Why this attempt failed, above the form, before anything else on the
           * card. `role="alert"` because it is the answer to something the person
           * just did, and they arrived on a fresh document with no other clue.
           *
           * Gated on `methods.settled` along with the block below, so the whole
           * "what happened, and what to try next" reads in one paint rather than
           * settling in two.
           */}
          {sent === null && methods.settled && refusal !== null ? (
            <Notice role="alert">{refusal}</Notice>
          ) : null}

          {sent === null ? (
            <RequestForm
              busy={busy}
              problem={problem}
              onSubmit={(address) => {
                void request(address)
              }}
            />
          ) : (
            <SentNotice
              sent={sent}
              busy={busy}
              onResend={() => {
                void request(sent.email)
              }}
              onChangeAddress={() => {
                setSent(null)
                setProblem(null)
              }}
            />
          )}

          {/*
           * The other doors, under the magic link and never over it — the link is
           * the one that works for everybody, and `CONTRACT.md` §2.7 is explicit
           * that these out-rank nothing.
           *
           * Empty until `/api/auth-methods` has answered, and empty for ever on a
           * deployment with no credentials — which is a correct portal, not a
           * degraded one. A failed read is the same as none.
           */}
          {sent === null && methods.settled && methods.providers.length > 0 ? (
            <>
              <Or />
              <Stack gap="tight">
                {methods.providers.map((provider) => (
                  <ProviderButton
                    key={provider}
                    provider={provider}
                    disabled={busy}
                    onClick={() => {
                      leaveFor(provider)
                    }}
                  />
                ))}
              </Stack>
            </>
          ) : null}

          <Small>
            <Muted>
              The link is good for <B>{String(MAGIC_LINK_TTL_SECONDS / 60)} minutes</B> and one use.
              If you are already signed in on this device you will land straight on your dashboard.
            </Muted>
          </Small>

          <Fold summary="Trouble signing in?">
            <Prose>
              Your invite may have expired, or you may be using an address that is not on the
              account. Ask whoever owns your account to re-invite you, or write to
              hello@madebyfareast.com.
            </Prose>
          </Fold>

          <Xs>
            <Muted>
              Studio staff:{' '}
              <Linkish asChild>
                <Link to="/studio">studio portal</Link>
              </Linkish>
            </Muted>
          </Xs>
        </SplashCard>
      </SplashForm>
    </Splash>
  )
}

/* -------------------------------------------------------------------- the form */

const EMAIL_INVALID = 'That does not look like an email address.'
const EMAIL_EMPTY = 'Enter the address we invoice.'

/**
 * The two refusals the form makes on its own. The server never sees these, and
 * they are the same two the hand-written version made — an empty address gets
 * the sentence that says what to type, not zod's "invalid email".
 */
const LoginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, EMAIL_EMPTY)
    .refine((value) => z.email().safeParse(value).success, EMAIL_INVALID),
})

function RequestForm({
  busy,
  problem,
  onSubmit,
}: {
  readonly busy: boolean
  readonly problem: string | null
  readonly onSubmit: (address: string) => void
}) {
  const form = useZodForm(LoginSchema, { defaultValues: { email: '' } })

  return (
    <form
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSubmit(values.email.trim())
        })(event)
      }}
    >
      <Input
        control={form.control}
        name="email"
        label="Work email"
        type="email"
        autoComplete="email"
        placeholder="deniz@casaturca.com"
      />
      {problem === null ? null : <Notice role="alert">{problem}</Notice>}
      <Button type="submit" variant="primary" disabled={busy}>
        {busy ? 'Sending…' : 'Email me a sign-in link'}
      </Button>
    </form>
  )
}

/* ------------------------------------------------------------------- the notice */

function SentNotice({
  sent,
  busy,
  onResend,
  onChangeAddress,
}: {
  readonly sent: Sent
  readonly busy: boolean
  readonly onResend: () => void
  readonly onChangeAddress: () => void
}) {
  const remaining = useCountdown(sent.resendAt)
  const ready = remaining === '0:00'

  return (
    <Notice role="status">
      <Stack gap="tight">
        <B>Check {sent.email}</B>
        <Small>
          <Dim>
            We sent a link a moment ago. It can take a minute — check spam before asking for
            another.
          </Dim>
        </Small>
        <ButtonRow>
          <Button size="sm" disabled={!ready || busy} onClick={onResend}>
            {ready ? 'Send another link' : `Resend in ${remaining}`}
          </Button>
          <Button size="sm" variant="ghost" onClick={onChangeAddress}>
            Use a different address
          </Button>
        </ButtonRow>
      </Stack>
    </Notice>
  )
}

/**
 * `m:ss` until the deadline, re-read from the clock every second.
 *
 * Re-read, not decremented: a tab that slept through the whole minute wakes with
 * `0:00` rather than with wherever it left off (`.docs/08-web-foundation.md`
 * §Edge cases).
 */
function useCountdown(deadline: Date): string {
  const [text, setText] = useState(() => formatCountdown(deadline, now()))

  useEffect(() => {
    setText(formatCountdown(deadline, now()))
    const id = setInterval(() => {
      setText(formatCountdown(deadline, now()))
    }, 1000)
    return () => {
      clearInterval(id)
    }
  }, [deadline])

  return text
}
